import React from "react";

/**
 * Reusable price display with strike-through original + highlighted offer + savings.
 *
 * Props:
 *   pricing  : { original, offer, save_amount, save_pct, applied? }   (canonical shape from API)
 *   layout   : "inline" | "stacked" | "card"                (visual variant)
 *   size     : "sm" | "md" | "lg"
 *   showSave : boolean (default true)                       (show "Save ₹X · Y% OFF")
 *   showFirstBadge : boolean (default true)                 (show "🎉 First Consultation Offer" pill when applied=='first')
 */
export default function PriceTag({
  pricing,
  layout = "inline",
  size = "md",
  showSave = true,
  showFirstBadge = true,
  testid,
}) {
  if (!pricing) return null;
  const { original, offer, save_amount, save_pct, applied } = pricing;
  const hasDiscount = save_amount > 0 && offer < original;
  const isFirst = applied === "first";

  const sizes = {
    sm: { offer: "text-base", original: "text-xs", save: "text-[10px]" },
    md: { offer: "text-2xl", original: "text-sm", save: "text-[11px]" },
    lg: { offer: "text-4xl", original: "text-base", save: "text-xs" },
  }[size];

  if (layout === "stacked") {
    return (
      <div data-testid={testid} className="flex flex-col">
        {hasDiscount && (
          <span className={`${sizes.original} text-slate-400 line-through font-medium`} data-testid={`${testid}-original`}>
            ₹{original.toLocaleString("en-IN")}
          </span>
        )}
        <span
          className={`${sizes.offer} font-extrabold text-[#0A2540] leading-none`}
          style={{ fontFamily: "Poppins" }}
          data-testid={`${testid}-offer`}
        >
          ₹{offer.toLocaleString("en-IN")}
        </span>
        {isFirst && showFirstBadge && (
          <span
            className={`${sizes.save} mt-1 inline-flex items-center gap-1 font-bold text-[#0A2540] bg-[#FBF4DF] border border-[#F1E3B5] rounded-full px-2 py-0.5 self-start`}
            data-testid={`${testid}-first-badge`}
          >
            🎉 First Consultation Offer
          </span>
        )}
        {showSave && hasDiscount && !isFirst && (
          <span
            className={`${sizes.save} mt-1 inline-flex items-center gap-1 font-bold text-emerald-700`}
            data-testid={`${testid}-save`}
          >
            Save ₹{save_amount.toLocaleString("en-IN")} · {save_pct}% OFF
          </span>
        )}
      </div>
    );
  }

  if (layout === "card") {
    return (
      <div data-testid={testid} className="flex flex-col gap-1">
        <div className="flex items-end gap-2 flex-wrap">
          <span
            className={`${sizes.offer} font-extrabold text-[#0A2540] leading-none`}
            style={{ fontFamily: "Poppins" }}
            data-testid={`${testid}-offer`}
          >
            ₹{offer.toLocaleString("en-IN")}
          </span>
          {hasDiscount && (
            <>
              <span className={`${sizes.original} text-slate-400 line-through font-medium leading-tight pb-0.5`} data-testid={`${testid}-original`}>
                ₹{original.toLocaleString("en-IN")}
              </span>
              {showSave && !isFirst && (
                <span
                  className={`${sizes.save} font-bold text-white bg-emerald-600 rounded-md px-1.5 py-0.5 leading-tight`}
                  data-testid={`${testid}-save-pct`}
                >
                  {save_pct}% OFF
                </span>
              )}
            </>
          )}
        </div>
        {isFirst && showFirstBadge && (
          <span
            className="text-[10px] font-bold inline-flex items-center gap-1 text-[#0A2540] bg-[#FBF4DF] border border-[#F1E3B5] rounded-full px-2 py-0.5 self-start"
            data-testid={`${testid}-first-badge`}
          >
            🎉 First Consultation Offer
          </span>
        )}
      </div>
    );
  }

  // inline (default)
  return (
    <span data-testid={testid} className="inline-flex items-baseline gap-1.5 flex-wrap">
      <span className={`${sizes.offer} font-extrabold text-[#0A2540]`} style={{ fontFamily: "Poppins" }} data-testid={`${testid}-offer`}>
        ₹{offer.toLocaleString("en-IN")}
      </span>
      {hasDiscount && (
        <span className={`${sizes.original} text-slate-400 line-through font-medium`} data-testid={`${testid}-original`}>
          ₹{original.toLocaleString("en-IN")}
        </span>
      )}
      {isFirst && showFirstBadge ? (
        <span
          className={`${sizes.save} font-bold inline-flex items-center gap-1 text-[#0A2540] bg-[#FBF4DF] border border-[#F1E3B5] rounded-full px-2 py-0.5`}
          data-testid={`${testid}-first-badge`}
        >
          🎉 First Consultation Offer
        </span>
      ) : (
        showSave && hasDiscount && (
          <span className={`${sizes.save} font-bold text-emerald-700`} data-testid={`${testid}-save-pct`}>
            {save_pct}% OFF
          </span>
        )
      )}
    </span>
  );
}

/** Convenience helper: pull the pricing for a given consultation type from a
 *  lawyer doc enriched by the backend.  Falls back to deriving from `fee` for
 *  any pre-discounted lawyer record (defensive). */
export function lawyerPricing(lawyer, type = "chat") {
  if (lawyer?.pricing?.[type]) return lawyer.pricing[type];
  // Defensive fallback (shouldn't usually happen since backend now always enriches)
  const mult = { chat: 1.0, audio: 1.2, video: 1.5 }[type] ?? 1.0;
  const original = Math.round((lawyer?.fee || 500) * mult);
  return { original, offer: original, save_amount: 0, save_pct: 0, applied: "none" };
}
